import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Header from './Header';
import Features from './Features';
import Problems from './Problems';
import Documentation from './Documentation';
import Footer from './Footer';
import { ink, sky, paper, paperMuted, borderInk, bodyMuted } from '../theme';

const navy = ink;
const navyMid = '#042248';
const chalk = 'rgba(255, 255, 255, 0.88)';
const lineSoft = 'rgba(255, 255, 255, 0.42)';

const ProductPageContainer = styled.div`
  min-height: 100vh;
  background: ${paperMuted};
  padding-top: 80px;
  font-family: 'Inter', system-ui, sans-serif;
`;

const HeroSection = styled.section`
  position: relative;
  padding: 5.5rem 0 4rem;
  background: radial-gradient(120% 90% at 50% -15%, ${navyMid} 0%, ${navy} 52%, #020f22 100%);
  overflow: hidden;
  color: ${chalk};

  @media (max-width: 768px) {
    padding: 4.75rem 0 3rem;
  }
`;

const PaperGrain = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.07;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  mix-blend-mode: overlay;
`;

const GraphPaper = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.14;
  background-image:
    linear-gradient(${lineSoft} 1px, transparent 1px),
    linear-gradient(90deg, ${lineSoft} 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: linear-gradient(90deg, transparent 0%, black 12%, black 88%, transparent 100%);
  filter: blur(0.35px);
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 clamp(16px, 4vw, 48px);
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 44rem;
  margin: 0 auto 2.5rem;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(1.65rem, 3.6vw, 2.85rem);
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #ffffff;
  margin-bottom: 1rem;
  text-shadow:
    0 0 42px rgba(180, 210, 255, 0.12),
    0 2px 0 rgba(0, 0, 0, 0.35);
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(0.88rem, 1.2vw, 1rem);
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 0.5rem;
`;

const HeroSubtitleRu = styled(motion.p)`
  font-size: clamp(0.95rem, 1.35vw, 1.08rem);
  font-weight: 500;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.72);
  max-width: 38rem;
  margin: 0 auto 1.5rem;
`;

const HeroDescription = styled(motion.div)`
  margin-top: 0.5rem;
  padding: 1.25rem 1.35rem;
  text-align: left;
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.045) 0%, rgba(255, 255, 255, 0) 52%);
  border: 1px solid rgba(255, 255, 255, 0.38);
  border-radius: 2px;
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.06),
    0 0 18px rgba(140, 190, 255, 0.06);
  position: relative;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(180deg, ${sky} 0%, rgba(142, 207, 255, 0.35) 100%);
    border-radius: 1px 0 0 1px;
    pointer-events: none;
  }
`;

const DescriptionText = styled.p`
  font-size: 0.92rem;
  line-height: 1.65;
  color: rgba(255, 255, 255, 0.58);
  margin: 0;
  padding-left: 0.5rem;
`;

const StatsContainer = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  max-width: 52rem;
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 20rem;
  }
`;

const StatCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 1.15rem 1rem 1.2rem;
  min-height: 108px;
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.045) 0%, rgba(255, 255, 255, 0) 52%);
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: -1px;
    border: 1px solid rgba(255, 255, 255, 0.38);
    border-radius: 2px;
    filter: blur(0.55px);
    opacity: 0.75;
    z-index: -1;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 1px solid rgba(255, 255, 255, 0.52);
    border-radius: 2px;
    box-shadow:
      0 0 0 1px rgba(255, 255, 255, 0.06),
      0 0 18px rgba(140, 190, 255, 0.06);
    pointer-events: none;
    z-index: -1;
  }
`;

const StatNumber = styled.div`
  font-size: clamp(1.15rem, 2.2vw, 1.45rem);
  font-weight: 800;
  letter-spacing: 0.04em;
  color: ${sky};
  margin-bottom: 0.45rem;
`;

const StatLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.35;
`;

const ProductInfoSection = styled.section`
  padding: 4.5rem 0;
  background: ${paper};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const InfoCard = styled(motion.div)`
  background: ${paper};
  border: 1px solid ${borderInk};
  border-radius: 2px;
  margin-bottom: 1.25rem;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(3, 26, 56, 0.05);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    box-shadow: 0 8px 24px rgba(3, 26, 56, 0.08);
    border-color: rgba(3, 26, 56, 0.32);
  }
`;

const InfoCardHead = styled.div`
  position: relative;
  padding: 14px 22px 16px calc(22px + 5px);
  overflow: hidden;
  background: linear-gradient(168deg, #0e3a63 0%, ${ink} 52%, #021427 100%);
  border-bottom: 1px solid rgba(142, 207, 255, 0.22);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.07);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    opacity: 0.12;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.4) 1px, transparent 1px);
    background-size: 12px 12px;
    pointer-events: none;
    filter: blur(0.2px);
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 5px;
    background: linear-gradient(180deg, ${sky} 0%, rgba(142, 207, 255, 0.35) 100%);
    box-shadow: 3px 0 12px rgba(142, 207, 255, 0.18);
    pointer-events: none;
  }
`;

const InfoTitle = styled.h3`
  position: relative;
  z-index: 1;
  margin: 0;
  padding-left: 0.4rem;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #f8fbff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.45);
`;

const InfoCardBody = styled.div`
  padding: 1.15rem 1.35rem 1.25rem;
`;

const InfoText = styled.p`
  margin: 0;
  color: ${bodyMuted};
  line-height: 1.65;
  font-size: 0.98rem;
`;

const ProductPage = () => {
  const infoBlocks = [
    { title: 'Для кого', text: 'DevOps-команды, архитекторы облачных инфраструктур.' },
    { title: 'Задача', text: 'Защита периметра и рабочих нагрузок в гибридных облаках.' },
    {
      title: 'Ключевая функция',
      text: 'Автоматическое обнаружение уязвимостей, мониторинг конфигураций безопасности и реагирование на инциденты в реальном времени.',
    },
    { title: 'Ценность', text: 'Соответствие требованиям регуляторов без замедления разработки.' },
  ];

  return (
    <ProductPageContainer>
      <Header />
      <HeroSection id="product-hero">
        <PaperGrain aria-hidden />
        <GraphPaper aria-hidden />
        <HeroInner>
          <HeroContent>
            <HeroTitle
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65 }}
            >
              e-node оазис (cloud)
            </HeroTitle>

            <HeroSubtitle
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.08 }}
            >
              Hybrid & Cloud Native Firewall
            </HeroSubtitle>

            <HeroSubtitleRu
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.14 }}
            >
              Система безопасности для облачных сред
            </HeroSubtitleRu>

            <HeroDescription
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.2 }}
            >
              <DescriptionText>
                e-node оазис (cloud) – это распределенная система управления сетевой безопасностью, функционирующая в современной гибридной – облачной и классической ИТ инфраструктуре. Система интегрируется со всеми современными средствами виртуализации и оркестрации, предоставляет инструменты автоматизации управления безопасностью, мониторинга состояния инфраструктуры и ее компонентов, развитые средства аналитики.
              </DescriptionText>
            </HeroDescription>
          </HeroContent>

          <StatsContainer
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.28 }}
          >
            <StatCard>
              <StatNumber>100%</StatNumber>
              <StatLabel>Гибридная инфраструктура</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>24/7</StatNumber>
              <StatLabel>Мониторинг безопасности</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>Zero Trust</StatNumber>
              <StatLabel>Модель безопасности</StatLabel>
            </StatCard>
          </StatsContainer>
        </HeroInner>
      </HeroSection>

      <ProductInfoSection>
        <Container>
          {infoBlocks.map((block, index) => (
            <InfoCard
              key={block.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.06 }}
            >
              <InfoCardHead>
                <InfoTitle>{block.title}</InfoTitle>
              </InfoCardHead>
              <InfoCardBody>
                <InfoText>{block.text}</InfoText>
              </InfoCardBody>
            </InfoCard>
          ))}
        </Container>
      </ProductInfoSection>

      <Features />
      <Problems />
      <Documentation />
      <Footer />
    </ProductPageContainer>
  );
};

export default ProductPage;
